/** Active preview visibility for Canvas generation nodes.
 *
 * Persisted output metadata may remain while overwrite-regenerate runs;
 * only the render layer hides the previous result during active execution. */

export const GENERATION_EXECUTION_STATUSES = new Set([
  'SUBMITTING',
  'QUEUED',
  'WAITING_FOR_ENGINE',
  'UPLOADING_ASSETS',
  'SUBMITTING_REQUEST',
  'RETRYING',
  'GENERATING',
])

/** Post-accept execution — task is created and in-flight on the backend. */
export const GENERATION_ACCEPTED_STATUSES = new Set([
  'QUEUED',
  'WAITING_FOR_ENGINE',
  'UPLOADING_ASSETS',
  'SUBMITTING_REQUEST',
  'RETRYING',
  'GENERATING',
])

export function isGenerationExecutionActive(status: string | undefined): boolean {
  return GENERATION_EXECUTION_STATUSES.has(status ?? '')
}

export function isGenerationAcceptedActive(status: string | undefined): boolean {
  return GENERATION_ACCEPTED_STATUSES.has(status ?? '')
}

/** True when overwrite-regenerate should hide the previous result preview. */
export function isReplaceGenerationPreviewSuppressed(
  hasExistingResult: boolean,
  status: string | undefined,
): boolean {
  if (!hasExistingResult) return isGenerationExecutionActive(status)
  return isGenerationAcceptedActive(status)
}

/** True while remounting with a persisted run id — avoid flashing the old preview. */
export function shouldHydrateRunAsGenerating(
  hasExistingResult: boolean,
  latestRunId: string | null | undefined,
  runHydrating: boolean,
): boolean {
  return Boolean(hasExistingResult && latestRunId && runHydrating)
}

/** VistoeraTASK-00139 — the background "hydrate/re-verify latest run"
 * fetch (catches an orphaned run, or resumes polling a genuinely
 * still-in-flight one) must only gate/hide the persisted preview
 * (via `runHydrating` → `shouldHydrateRunAsGenerating` above) when the
 * locally persisted `status` is NOT already terminal. Forcing this true
 * unconditionally on every remount of an already-COMPLETED/FAILED node
 * was the real cause of "result flashing/loading" on Canvas refresh: it
 * hides a known-good persisted result behind a spinner until a network
 * round-trip completes, and that round-trip shares backend capacity with
 * poster backfill (see `videoPosterLoadGate.ts`) and can take many
 * seconds when a Canvas has several historical video nodes. */
export function shouldForceRunHydratingOnMount(hasExistingResult: boolean, status: string | undefined): boolean {
  if (!hasExistingResult) return false
  return status !== 'COMPLETED' && status !== 'FAILED'
}

/** Resolve which persisted output id should drive the media preview URL. */
export function resolveGenerationDisplayOutputId(args: {
  generatedOutputId?: string
  previousOutputId?: string
}): string | undefined {
  return args.generatedOutputId || args.previousOutputId
}

/** True when the node should render its persisted output in the media body. */
export function shouldShowActiveGenerationOutput(
  displayOutputId: string | undefined,
  status: string | undefined,
  options?: { hasExistingResult?: boolean; runHydrating?: boolean; latestRunId?: string | null },
): boolean {
  if (!displayOutputId) return false
  const hasExistingResult = options?.hasExistingResult ?? true
  if (shouldHydrateRunAsGenerating(hasExistingResult, options?.latestRunId, options?.runHydrating ?? false)) {
    return false
  }
  if (isReplaceGenerationPreviewSuppressed(hasExistingResult, status)) return false
  return true
}
